import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Search, Sparkles, AlertCircle, CheckCircle } from '../../../components/icons';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { isUserCoach, checkCoachAIAccess } from '../../../lib/auth';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { characteristics } from '../../../data/characteristics';
import { harmonicLevels } from '../../../data/harmonicLevels';
import type { AssessmentResult } from '../../../types/results';

interface RespondentWithResult {
  userId: string;
  name: string;
  email: string;
  result: AssessmentResult;
}

export function ManualAIAnalysisTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [respondents, setRespondents] = useState<RespondentWithResult[]>([]);
  const [selectedRespondent, setSelectedRespondent] = useState<RespondentWithResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      try {
        const isCoach = await isUserCoach(user.id);
        if (!isCoach) {
          navigate('/tr/login');
          return;
        }

        const aiAccess = await checkCoachAIAccess(user.id);
        if (!aiAccess) {
          setHasAccess(false);
          setIsLoading(false);
          return;
        }

        setHasAccess(true);

        const { data: coach } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coach) {
          navigate('/tr/login');
          return;
        }

        const { data: respondentsData } = await supabase
          .from('respondents')
          .select('user_id, users!respondents_user_id_fkey(full_name, email)')
          .eq('coach_id', coach.id);

        const respondentsWithResults: RespondentWithResult[] = [];

        for (const resp of respondentsData || []) {
          const { data: resultData } = await supabase
            .from('results')
            .select('*')
            .eq('user_id', resp.user_id)
            .order('completed_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (resultData) {
            respondentsWithResults.push({
              userId: resp.user_id,
              name: (resp.users as any)?.full_name || (resp.users as any)?.email || 'Unknown',
              email: (resp.users as any)?.email || '',
              result: {
                id: resultData.id,
                userId: resultData.user_id,
                coachId: resultData.coach_id || undefined,
                partnerId: resultData.partner_id || undefined,
                scores: resultData.scores as any,
                harmonicScores: resultData.harmonic_scores as any,
                completedAt: new Date(resultData.completed_at || Date.now()),
                aiAnalysis: resultData.ai_analysis || undefined,
                coachAiAnalysis: resultData.coach_ai_analysis || undefined,
                version: resultData.version || 1,
                originalResultId: resultData.original_result_id || undefined
              }
            });
          }
        }

        setRespondents(respondentsWithResults);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const formatDataForAI = (result: AssessmentResult) => {
    const characteristicDetails = result.scores.map(score => {
      const characteristic = characteristics.find(c => c.id === score.characteristicId);
      return `${characteristic?.name}: ${Math.round(score.normalized)}%`;
    }).join('\n');

    const dominantHarmonic = result.harmonicScores.reduce((prev, current) =>
      current.normalized > prev.normalized ? current : prev
    );
    const dominantLevel = harmonicLevels.find(level => level.id === dominantHarmonic.levelId);

    const harmonicDetails = result.harmonicScores
      .sort((a, b) => b.normalized - a.normalized)
      .map(score => {
        const level = harmonicLevels.find(l => l.id === score.levelId);
        return `${level?.name}: ${Math.round(score.normalized)}%`;
      }).join('\n');

    return `
Assessment Results:

Characteristics:
${characteristicDetails}

Harmonic Scale:
Dominant Level: ${dominantLevel?.name} (${Math.round(dominantHarmonic.normalized)}%)

Detailed Harmonic Levels:
${harmonicDetails}

Based on these results, provide a comprehensive analysis of this person's potential, strengths, and areas for development. Include specific recommendations for personal growth.
`;
  };

  const generateAnalysis = async () => {
    if (!selectedRespondent) return;

    setIsGenerating(true);
    setError(null);

    try {
      const prompt = formatDataForAI(selectedRespondent.result);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ prompt })
      });

      if (!response.ok) {
        throw new Error('Failed to generate analysis');
      }

      const data = await response.json();
      setAnalysis(data.analysis);

      const { error: updateError } = await supabase
        .from('results')
        .update({
          coach_ai_analysis: data.analysis,
          coach_ai_analysis_generated_at: new Date().toISOString()
        })
        .eq('id', selectedRespondent.result.id);

      if (updateError) {
        throw new Error('Failed to save analysis: ' + updateError.message);
      }
    } catch (err) {
      console.error('Error generating analysis:', err);
      setError('YZ analizi olusturulamadi. Lutfen tekrar deneyin.');
    } finally {
      setIsGenerating(false);
    }
  };

  const selectRespondent = (respondent: RespondentWithResult) => {
    setSelectedRespondent(respondent);
    setAnalysis(respondent.result.coachAiAnalysis || null);
  };

  const filteredRespondents = respondents.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Yukleniyor..." />;
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="freud-heading-xl text-chocolate-900 mb-4">
              Erisim Kisitli
            </h1>
            <p className="text-chocolate-500 mb-6">
              Manuel YZ Analizi yalnizca Gelismis veya Partner abonelik katmanina sahip koclar icin kullanilabilir.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-sage-600" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">YZ Analizi</h1>
              <p className="text-sm text-chocolate-500">Katilimcilariniz icin YZ analizi olusturun</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="freud-card-elevated overflow-hidden">
              <div className="p-4 border-b border-cream-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-chocolate-400" />
                  <input
                    type="text"
                    placeholder="Katilimci ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="freud-input pl-10"
                  />
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {filteredRespondents.map((respondent) => (
                  <button
                    key={respondent.userId}
                    onClick={() => selectRespondent(respondent)}
                    className={`w-full px-4 py-3 text-left hover:bg-cream-50 transition-colors border-b border-cream-200 last:border-b-0 ${
                      selectedRespondent?.userId === respondent.userId ? 'bg-sage-50' : ''
                    }`}
                  >
                    <div className="font-medium text-chocolate-900">{respondent.name}</div>
                    <div className="text-sm text-chocolate-500">{respondent.email}</div>
                    {respondent.result.coachAiAnalysis && (
                      <span className="inline-flex items-center gap-1 mt-1 text-xs text-sage-600">
                        <CheckCircle className="w-3 h-3" />
                        Analiz mevcut
                      </span>
                    )}
                  </button>
                ))}
                {filteredRespondents.length === 0 && (
                  <div className="p-4 text-center text-chocolate-500">
                    Kullanilabilir katilimci yok
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {!selectedRespondent ? (
              <div className="freud-card-elevated p-12 text-center">
                <Brain className="w-16 h-16 text-cream-400 mx-auto mb-4" />
                <h2 className="freud-heading-xl text-chocolate-900 mb-2">
                  Katilimci Sec
                </h2>
                <p className="text-chocolate-500">
                  YZ analizi olusturmak icin bir katilimci secin.
                </p>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="freud-card-elevated p-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="freud-heading-xl text-chocolate-900">{selectedRespondent.name}</h2>
                    <p className="text-chocolate-500">{selectedRespondent.email}</p>
                  </div>
                  <button
                    onClick={generateAnalysis}
                    disabled={isGenerating}
                    className="freud-btn-primary inline-flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Olusturuluyor...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        {analysis ? 'Yeniden Olustur' : 'Analiz Olustur'}
                      </>
                    )}
                  </button>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                    {error}
                  </div>
                )}

                {analysis ? (
                  <div className="prose max-w-none">
                    <ReactMarkdown
                      components={{
                        h1: ({ children }) => (
                          <h1 className="text-2xl font-bold text-chocolate-900 mt-8 mb-4 pb-3 border-b border-cream-300 first:mt-0">{children}</h1>
                        ),
                        h2: ({ children }) => (
                          <h2 className="text-xl font-bold text-chocolate-800 mt-8 mb-3 pb-2 border-b border-cream-200">{children}</h2>
                        ),
                        h3: ({ children }) => (
                          <h3 className="text-lg font-semibold text-chocolate-800 mt-6 mb-2">{children}</h3>
                        ),
                        p: ({ children }) => (
                          <p className="text-chocolate-700 leading-[1.75] mb-4 text-[15px]">{children}</p>
                        ),
                        li: ({ children }) => (
                          <li className="text-chocolate-700 text-[15px] leading-[1.75] pl-1">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-semibold text-chocolate-900">{children}</strong>
                        ),
                        blockquote: ({ children }) => (
                          <blockquote className="border-l-4 border-sage-300 bg-sage-50/50 pl-4 py-3 pr-4 my-4 rounded-r-lg">
                            {children}
                          </blockquote>
                        ),
                      }}
                    >{analysis}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-center py-12 text-chocolate-500">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Bu katilimci icin YZ destekli bir analiz olusturmak icin "Analiz Olustur" dugmesine tiklayin.</p>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
